import {useEffect,useMemo,useState} from 'react';
type Template={key:string;title:string;kind:'email'|'snippet'|'service_instruction';
  sourceLocation:string;guidance:string;explicitSubject:boolean;
  subject:string;body:string;enabled:boolean;version:number;customized:boolean};
export function AutoEmailsWorkspace({locationCode}:{locationCode:string}){
  const [templates,setTemplates]=useState<Template[]>([]);
  const [status,setStatus]=useState<'loading'|'ready'|'error'>('loading');
  const [view,setView]=useState<'emails'|'service'|'sms'>('emails');
  const [selected,setSelected]=useState<string|null>(null);
  const [subject,setSubject]=useState('');
  const [body,setBody]=useState('');
  const [busy,setBusy]=useState(false);
  const [notice,setNotice]=useState('');
  useEffect(()=>{
    const controller=new AbortController();
    setStatus('loading');
    void fetch('/api/v1/staff/email-templates?'+
      new URLSearchParams({location:locationCode}),{
        credentials:'same-origin',cache:'no-store',signal:controller.signal,
      }).then(async response=>{
        if(!response.ok)throw Error('Templates unavailable');
        const data=await response.json() as {ok:boolean;data?:{templates:Template[]}};
        if(!data.ok||!Array.isArray(data.data?.templates))throw Error('Templates unavailable');
        if(!controller.signal.aborted){setTemplates(data.data.templates);setStatus('ready');}
      }).catch(()=>{if(!controller.signal.aborted)setStatus('error');});
    return ()=>controller.abort();
  },[locationCode]);
  const sequence=[
    'sale_receipt_email_snippet','membership_pitch_snippet',
    'service_credit_expiring_snippet','waiver_request_snippet',
    'reservation_confirmation_email_text','reservation_moved_email_text',
    'reservation_reminder_email_text','reservation_no_show_email_text',
    'reservation_cancellation_email_text',
    'reservation_cancellation_and_charge_email_text',
    'reservation_follow_up_email_text','booking_abandonment_email_text',
    'store_abandonment_email_text','waitlist_entry_unfulfilled_email_text',
    'gift_card_recipient_email_text','membership_charge_failing_email_text',
    'member_reactivation_email_text','birthday_non_members','birthday_members',
    'reactivation_7_day','reactivation_14_day','reactivation_30_day',
    'reactivation_60_day','reactivation_90_day','reactivation_180_day',
    'reactivation_365_day','refund_approved','refund_declined',
    'float_appointments','contrast_therapy_appointments','infrared_sauna_appointments',
    'cold_plunge_appointments','zero_gravity_massage_chair_appointments',
    'red_light_therapy_appointments','oxygen_bar_appointments',
    'guided_meditation_hypnosis_appointments',
  ];
  const visible=useMemo(()=>templates.filter(x=>
    !['birthday_email_text','customer_reactivation_emails'].includes(x.key)&&
    (view==='service'?x.kind==='service_instruction':
      view==='emails'?x.kind!=='service_instruction':false))
      .sort((a,b)=>sequence.indexOf(a.key)-sequence.indexOf(b.key)),
    [templates,view]);
  const choose=(item:Template)=>{
    if(selected===item.key){setSelected(null);return;}
    setSelected(item.key);setSubject(item.subject);setBody(item.body);setNotice('');
  };
  const save=async(item:Template)=>{
    setBusy(true);setNotice('');
    try{
      const response=await fetch('/api/v1/staff/email-templates/'+encodeURIComponent(item.key),{
        method:'PATCH',credentials:'same-origin',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:locationCode,expectedVersion:item.version,subject,body}),
      });
      if(!response.ok)throw Error(response.status===403?
        'Only an ECFS owner can save email templates.':
        response.status===409?'This template changed elsewhere. Reload it.':'Save failed.');
      const json=await response.json() as {ok:boolean;data?:{version:number}};
      if(!json.ok||!json.data)throw Error('Save failed.');
      setTemplates(previous=>previous.map(x=>x.key===item.key?
        {...x,subject,body,version:json.data!.version,customized:true}:x));
      setNotice('Saved for this location. No email has been sent.');
    }catch(e){setNotice(e instanceof Error?e.message:'Save failed.');}
    finally{setBusy(false);}
  };
  const locationName=locationCode==='PR'?'Princeton':
    locationCode==='WC'?'West Chester':locationCode==='YD'?'Yardley':locationCode;
  return <section className="ecfs-auto-emails" aria-label="Email Templates">
    <h1>East Coast Float Spa {locationName} Email Templates</h1>
    <div className="ecfs-auto-emails-links">
      <button type="button" onClick={()=>{setView('service');setSelected(null);}}>
        edit per-service type instructions</button>
      <button type="button" onClick={()=>{setView('emails');setSelected(null);}}>
        email templates</button>
      <button type="button" onClick={()=>{setView('sms');setSelected(null);}}>
        SMS templates</button>
    </div>
    <p>Emails can use verified template variables such as
      {' <NAME>, <SHOP:NAME>, <SHOP:EMAIL>, <SHOP:PHONE>.'}
      This is the owner-supplied template library. Delivery is not yet connected.</p>
    {status==='loading'&&<p role="status">Loading templates…</p>}
    {status==='error'&&<p role="alert">Email template library could not be loaded.</p>}
    {status==='ready'&&view==='sms'&&<p role="status">
      The provided document contains email templates and service instructions,
      but no separate SMS template bodies. None have been invented.</p>}
    {status==='ready'&&<div className="ecfs-email-template-list">
      {visible.map(item=><section key={item.key} className="ecfs-email-template">
        {item.key==='reactivation_7_day'&&
          <h3>Customer Reactivation Emails</h3>}
        <button type="button" title={item.guidance||'Edit the copy and subject for this template.'}
          aria-expanded={selected===item.key}
          onClick={()=>choose(item)}>
          {item.title}{item.guidance&&<span aria-hidden="true"> ⓘ</span>}
          {item.body===''&&<em>Content not provided</em>}
        </button>
        {selected===item.key&&<div className="ecfs-email-template-editor">
          {item.sourceLocation!=='SHARED'&&item.sourceLocation!==locationCode&&
            !item.customized&&<p role="status">
              Source copy references a different location ({item.sourceLocation}).
              Review and replace local contact details before using it here.</p>}
          {item.kind==='email'&&<label>Subject Line
            <input type="text" aria-label="Email subject" maxLength={320}
              value={subject} onChange={event=>setSubject(event.target.value)}/>
            {!item.explicitSubject&&<small>
              Draft subject was supplied by the app because the source had none.</small>}
          </label>}
          <label>{item.kind==='service_instruction'?'Service Type Instructions':
            item.kind==='snippet'?'Snippet':'Email Body'}
            <textarea aria-label="Template body" value={body} rows={12}
              onChange={event=>setBody(event.target.value)}/>
          </label>
          <div className="ecfs-template-controls">
            <button type="button" className="ecfs-template-save"
              disabled={busy} onClick={()=>void save(item)}>{busy?'Saving…':'Save'}</button>
            {item.kind==='email'&&<button type="button" disabled
              title="A verified outgoing mail provider and self-preview sending have not been connected">
              Send Preview</button>}
            {item.kind==='email'&&<label>
              <input type="checkbox" checked={false} disabled readOnly/> Enabled
              <small>Sending is disabled until verified integration.</small>
            </label>}
          </div>
          {notice&&<p role="status">{notice}</p>}
        </div>}
      </section>)}
    </div>}
  </section>;
}
